
import { Button } from '@/components/ui/button';
import { CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BlogContentTab } from '@/components/blog/BlogContentTab';
import { BlogItinerariesTab } from '@/components/blog/BlogItinerariesTab';
import { BlogPackingTab } from '@/components/blog/BlogPackingTab';
import { BlogSeoTab } from '@/components/blog/BlogSeoTab';
import { BlogTagsTab } from '@/components/blog/BlogTagsTab';
import { BlogPostFormData } from '@/hooks/blog/useBlogPostForm';
import { Database } from '@/integrations/supabase/types';
import { Save } from 'lucide-react';

type ContentStatus = Database['public']['Enums']['content_status'];

interface BlogPostFormProps {
  formData: BlogPostFormData;
  selectedItineraryIds: string[];
  setSelectedItineraryIds: (ids: string[]) => void;
  selectedPackingListId?: string;
  setSelectedPackingListId: (id?: string) => void;
  selectedTagIds: string[];
  setSelectedTagIds: (ids: string[]) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  loading: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (name: string, value: ContentStatus) => void;
  handleImageUploaded: (imagePath: string) => void;
  handleDateTimeChange: (dateTime: Date | undefined) => void;
  handleContentChange?: (html: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  submitLabel?: string;
  blogPostId: string;
}

export function BlogPostForm({
  formData,
  selectedItineraryIds,
  setSelectedItineraryIds,
  selectedPackingListId,
  setSelectedPackingListId,
  selectedTagIds,
  setSelectedTagIds,
  activeTab,
  setActiveTab,
  loading,
  handleChange,
  handleSelectChange,
  handleImageUploaded,
  handleDateTimeChange,
  handleContentChange,
  onSubmit,
  onCancel,
  submitLabel = "Create Post",
  blogPostId
}: BlogPostFormProps) {
  return (
    <form onSubmit={onSubmit}>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 mb-4">
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
          <TabsTrigger value="itineraries">Itineraries</TabsTrigger>
          <TabsTrigger value="packing">What to Pack</TabsTrigger>
        </TabsList>
        
        <TabsContent value="content">
          <BlogContentTab 
            formData={formData}
            handleChange={handleChange}
            handleSelectChange={handleSelectChange}
            handleImageUploaded={handleImageUploaded}
            handleDateTimeChange={handleDateTimeChange}
            handleContentChange={handleContentChange}
          />
        </TabsContent>
        
        <TabsContent value="seo">
          <BlogSeoTab 
            formData={formData}
            handleChange={handleChange}
          />
        </TabsContent>
        
        <TabsContent value="tags">
          <BlogTagsTab 
            selectedTagIds={selectedTagIds}
            onChange={setSelectedTagIds}
          />
        </TabsContent>
        
        <TabsContent value="itineraries">
          <BlogItinerariesTab 
            selectedItineraryIds={selectedItineraryIds}
            onChange={setSelectedItineraryIds}
          />
        </TabsContent>
        
        <TabsContent value="packing">
          <BlogPackingTab 
            blogPostId={blogPostId}
            selectedPackingListId={selectedPackingListId}
            onSelect={setSelectedPackingListId}
          />
        </TabsContent>
        
        <CardFooter className="flex justify-between mt-6">
          <Button 
            variant="outline" 
            onClick={onCancel}
            type="button"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {submitLabel === "Create Post" ? "Creating..." : "Saving..."}
              </>
            ) : (
              <>
                {submitLabel !== "Create Post" && <Save className="h-4 w-4 mr-2" />}
                {submitLabel}
              </>
            )}
          </Button>
        </CardFooter>
      </Tabs>
    </form>
  );
}
